import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
    Grid,
    Typography,
    Alert,
    Button,
    Tooltip
} from '@mui/material';
import { toast } from 'react-toastify';
import DateRangePicker from '@/shared/components/ui/CustomSearch/DateRangePicker';
import { useTableFilter } from "@/shared";
import Flatpickr from "react-flatpickr";
import { format } from "date-fns";
import { Search } from "@mui/icons-material";
import { useTableColumnsReportesContratosGolondrinas } from "@/shared/hooks/useTableColumnsReportesContratosGolondrinas";
import { useObtenerReportesContratosGolondrinas } from "@/shared/actions/erp";
import { GeneralTable } from "@/shared/components/ui";
import { exportExcel, mapReporteContratosGolondrinasToExcel, } from "../function/exportarExcel";
import { RiFileExcel2Fill } from "react-icons/ri"

export default function TabReportesContratosGolondrinas() {

    const flatpickrRef = useRef<Flatpickr>(null);
    // Calcular fechas del mes actual
    const { firstDayCurrentMonth, currentDay } = useMemo(() => {
        const today = new Date();
        const year = today.getFullYear();
        const month = today.getMonth();
        // Primer día del mes actual
        // (JavaScript cuenta los meses desde 0, así que 'month' es el mes actual)
        const firstDay = new Date(year, month, 1);
        return {
            firstDayCurrentMonth: format(firstDay, "yyyy-MM-dd"),
            currentDay: format(today, "yyyy-MM-dd")
        };
    }, []);

    const {
        dateBefore,
        dateAfter,
        onChangeRangeDate,
    } = useTableFilter();

    // Cargar datos al montar (con rango del mes anterior)
    useEffect(() => {
        if (!dateAfter && !dateBefore) {
            // Establecer rango del mes anterior por defecto
            const startDate = new Date(firstDayCurrentMonth);
            const endDate = new Date(currentDay);
            onChangeRangeDate([startDate, endDate]);
        }
    }, []);

    const handleSearch = async () => {
        loadData()
    };

    // Estados de datos y carga
    const [allData, setAllData] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    // Paginación para Material React Table (manejada internamente por la tabla)
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    });

    const filteredData = useMemo(() => {
        let data = allData;
        return data;
    }, [allData]);

    // Paginación en memoria optimizada - solo pasamos las filas visibles a la tabla
    // Esto mejora drásticamente el rendimiento porque GeneralTable solo renderiza 10-50 filas en vez de 4812
    const paginatedData = useMemo(() => {
        const startIdx = pagination.pageIndex * pagination.pageSize;
        const endIdx = startIdx + pagination.pageSize;
        return filteredData.slice(startIdx, endIdx);
    }, [filteredData, pagination.pageIndex, pagination.pageSize]);

    const { columnBase } = useTableColumnsReportesContratosGolondrinas({ pagination });

    // AbortController para cancelar requests anteriores
    const abortControllerRef = useRef<AbortController | null>(null);

    // Hook para obtener datos
    const obtenerReporteContratosGolondrinasMutation = useObtenerReportesContratosGolondrinas();

    // Cargar datos con filtros
    const loadData = useCallback(async () => {
        // Cancelar request anterior si existe
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        setIsLoading(true);
        try {
            const filters: any = {
                fecha_desde: dateAfter || firstDayCurrentMonth,
                fecha_hasta: dateBefore || currentDay,
            };

            const response = await obtenerReporteContratosGolondrinasMutation.mutateAsync(filters);

            // Verificar si fue abortado
            if (abortControllerRef.current?.signal.aborted) {
                return;
            }

            if (response.data && Array.isArray(response.data)) {
                setAllData(response.data);
                // Resetear a la primera página cuando se cargan nuevos datos
                setPagination(prev => ({ ...prev, pageIndex: 0 }));
            } else {
                toast.error("Estructura de respuesta inesperada");
            }
        } catch (err: any) {
            if (err?.name === 'AbortError' || abortControllerRef.current?.signal.aborted) {
                return; // Request cancelado, no mostrar error
            }
            // Manejar error de rango de fechas inválido
            if (err?.response?.data?.code === 'INVALID_DATE_RANGE') {
                toast.error(err.response.data.message || "Rango de fechas inválido");
            } else {
                toast.error(err?.message || "Error al cargar datos");
            }
        } finally {
            setIsLoading(false);
        }
    }, [dateAfter, dateBefore, firstDayCurrentMonth, currentDay]);

    return (
        <>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Alert severity="info" sx={{ bgcolor: "info.light" }}>
                        <Typography variant="body2">
                            <strong>Información:</strong> Se mostrarán los datos de los clientes golondrinas (Reportados Equifax)
                        </Typography>
                    </Alert>
                </Grid>
                <Grid item xs={12}>
                    <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <DateRangePicker
                                size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
                                flatpickrRef={flatpickrRef}
                                onChange={(range) => onChangeRangeDate(range)}
                                label="Rango de fechas"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={2}>
                            <Button
                                disabled={isLoading}
                                variant="contained"
                                color="primary"
                                size="medium"
                                startIcon={<Search />}
                                onClick={handleSearch}
                                sx={{
                                    flex: { xs: 1, sm: '0 1 auto' },
                                    minWidth: { xs: 'auto', sm: 140 },
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    boxShadow: 2,
                                    '&:hover': { boxShadow: 4 }
                                }}
                            >
                                Consultar
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <Grid container spacing={1} justifyContent={'flex-end'}>
                        <Tooltip title="Click">
                            <Button
                                variant="outlined"
                                color="success"
                                onClick={() => exportExcel(mapReporteContratosGolondrinasToExcel(filteredData), 'reporte_contratos_golondrinas')}
                                startIcon={
                                    <RiFileExcel2Fill />
                                }
                            >
                                {'Exportar a Excel'}
                            </Button>
                        </Tooltip>
                    </Grid>

                    <GeneralTable
                        columns={columnBase}
                        data={paginatedData}
                        isLoading={isLoading}
                        pagination={pagination}
                        onPaging={setPagination}
                        rowCount={filteredData.length}
                        enableColumnActions={false}
                        enableEditing={false}
                        enableGlobalFilter={false}
                        enableColumnFilters={true}
                    />
                </Grid>

            </Grid>
        </>
    );
}
